import React, { useCallback, useMemo, useState } from 'react';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Center,
  FormControl,
  FormLabel,
  Heading,
  Input,
  Text,
  VStack,
} from '@chakra-ui/react';
import { useLocation } from 'react-router-dom';
import { useKeycloak } from '@react-keycloak/web';
import { MotionBox, variants } from '../components/motion';
import getVerificationServiceConfig from '../util/verificationService';

const RESET_ERROR_MESSAGES = {
  token_expired: 'The reset link has expired. Please request a new password reset.',
  token_invalid: 'The reset link is invalid or has already been used. Request a new link to continue.',
  weak_password: 'The new password does not meet the security requirements.',
  password_reuse: 'You cannot reuse a recent password. Choose a different one.',
};

const parseErrorResponse = async (response) => {
  try {
    const data = await response.json();
    return {
      code: data?.code,
      message: data?.message,
    };
  } catch (err) {
    try {
      const text = await response.text();
      return { code: '', message: text };
    } catch (innerErr) {
      return { code: '', message: '' };
    }
  }
};

const getResetErrorMessage = (code, fallback) => {
  if (code && RESET_ERROR_MESSAGES[code]) {
    return RESET_ERROR_MESSAGES[code];
  }
  return fallback || 'Unable to reset your password. Please try again.';
};

const useResetToken = (search) => {
  const params = useMemo(() => new URLSearchParams(search), [search]);
  return params.get('token')?.trim() || '';
};

export default function PasswordReset() {
  const location = useLocation();
  const token = useResetToken(location.search);
  const { keycloak } = useKeycloak();
  const { passwordResetEndpoint } = useMemo(() => getVerificationServiceConfig(), []);

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [status, setStatus] = useState(token ? 'idle' : 'error'); // idle | submitting | success | error
  const [message, setMessage] = useState(token ? '' : 'Invalid or missing reset token. Please request a new password reset.');

  const isConfigured = Boolean(passwordResetEndpoint);
  const hasToken = Boolean(token);
  const passwordsMatch = password === confirmPassword;
  const canSubmit = isConfigured && hasToken && Boolean(password) && Boolean(confirmPassword);

  const handleSubmit = useCallback(async (event) => {
    event.preventDefault();

    if (!hasToken) {
      setStatus('error');
      setMessage('Invalid or missing reset token. Please request a new password reset.');
      return;
    }

    if (!password || !confirmPassword) {
      setStatus('error');
      setMessage('Please enter and confirm your new password.');
      return;
    }

    if (password !== confirmPassword) {
      setStatus('error');
      setMessage('Passwords do not match.');
      return;
    }

    if (!passwordResetEndpoint) {
      setStatus('error');
      setMessage('Password reset service is not configured. Contact support.');
      return;
    }

    setStatus('submitting');
    setMessage('');

    try {
      const response = await fetch(passwordResetEndpoint, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });

      if (!response.ok) {
        const { code, message: responseMessage } = await parseErrorResponse(response);
        throw new Error(getResetErrorMessage(code, responseMessage));
      }

      const body = await response.json().catch(() => null);
      if (!body || body.status !== 'reset') {
        throw new Error('Unexpected response from password reset service.');
      }

      setStatus('success');
      setMessage('Password updated successfully. Redirecting to login…');

      await keycloak?.login({ redirectUri: window.location.origin });
    } catch (err) {
      setStatus('error');
      setMessage(err?.message || 'Unable to reset your password. Please try again.');
    }
  }, [confirmPassword, hasToken, keycloak, password, passwordResetEndpoint, token]);

  return (
    <Center minH="100vh" bg="gray.50" px={4}>
      <MotionBox
        as="form"
        onSubmit={handleSubmit}
        maxW="md"
        w="100%"
        p={8}
        borderWidth="1px"
        borderRadius="lg"
        bg="white"
        boxShadow="md"
        variants={variants.item}
        initial="hidden"
        animate="show"
      >
        <VStack spacing={5} align="stretch" textAlign="left">
          <Box textAlign="center">
            <Heading size="lg" mb={2}>Choose a new password</Heading>
            <Text color="gray.600">
              Enter and confirm your new password to finish resetting your account.
            </Text>
          </Box>

          {!isConfigured && (
            <Alert status="warning" borderRadius="md">
              <AlertIcon />
              Password reset service is not configured. Contact support.
            </Alert>
          )}

          {message && (
            <Alert status={status === 'success' ? 'success' : 'error'} borderRadius="md">
              <AlertIcon />
              {message}
            </Alert>
          )}

          <FormControl isRequired isDisabled={!hasToken}>
            <FormLabel>New password</FormLabel>
            <Input
              type="password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
              placeholder="Enter new password"
              autoComplete="new-password"
            />
          </FormControl>

          <FormControl isRequired isDisabled={!hasToken}>
            <FormLabel>Confirm new password</FormLabel>
            <Input
              type="password"
              value={confirmPassword}
              onChange={(event) => setConfirmPassword(event.target.value)}
              placeholder="Re-enter new password"
              autoComplete="new-password"
            />
          </FormControl>

          <Button
            type="submit"
            colorScheme="blue"
            isLoading={status === 'submitting'}
            isDisabled={!canSubmit || status === 'submitting'}
          >
            Reset password
          </Button>
        </VStack>
      </MotionBox>
    </Center>
  );
}
